import type { ComponentConfig, FormConfig } from '@devfactory/core';
import { FormRenderer } from './FormRenderer';

// =============================================================================
// COMPONENT REGISTRY
// =============================================================================

type ComponentType = React.FC<{ config: ComponentConfig; forms: FormConfig[] }>;

const componentRegistry: Record<string, ComponentType> = {
  // Layout Components
  container: ContainerComponent,
  grid: GridComponent,
  card: CardComponent,
  section: SectionComponent,

  // Content Components
  heading: HeadingComponent,
  text: TextComponent,
  image: ImageComponent,
  divider: DividerComponent,

  // Interactive Components
  button: ButtonComponent,
  link: LinkComponent,
  form: FormComponent,

  // Data Components
  table: TableComponent,
  list: ListComponent,
  stats: StatsComponent,
  chart: ChartComponent,

  // Navigation
  tabs: TabsComponent,
  breadcrumb: BreadcrumbComponent,
};

// =============================================================================
// COMPONENT RENDERER
// =============================================================================

interface ComponentRendererProps {
  config: ComponentConfig;
  forms: FormConfig[];
}

export function ComponentRenderer({ config, forms }: ComponentRendererProps) {
  const Component = componentRegistry[config.type];

  if (!Component) {
    return (
      <div className="p-4 border border-dashed border-yellow-400 bg-yellow-50 rounded-md">
        <p className="text-sm text-yellow-700">
          Unknown component type: <code className="font-mono">{config.type}</code>
        </p>
      </div>
    );
  }

  return <Component config={config} forms={forms} />;
}

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

function ContainerComponent({ config, forms }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { className = '', maxWidth = '7xl', padding = true } = config.props as {
    className?: string;
    maxWidth?: string;
    padding?: boolean;
  };

  const maxWidthClasses: Record<string, string> = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '4xl': 'max-w-4xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full',
  };

  return (
    <div
      className={`mx-auto ${maxWidthClasses[maxWidth] || 'max-w-7xl'} ${
        padding ? 'px-4 sm:px-6 lg:px-8' : ''
      } ${className}`}
    >
      {config.children?.map((child: ComponentConfig, index: number) => (
        <ComponentRenderer key={index} config={child} forms={forms} />
      ))}
    </div>
  );
}

function GridComponent({ config, forms }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { cols = 1, gap = 4, className = '' } = config.props as {
    cols?: number;
    gap?: number;
    className?: string;
  };

  const colsClasses: Record<number, string> = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
    6: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6',
  };

  return (
    <div className={`grid ${colsClasses[cols] || 'grid-cols-1'} gap-${gap} ${className}`}>
      {config.children?.map((child: ComponentConfig, index: number) => (
        <ComponentRenderer key={index} config={child} forms={forms} />
      ))}
    </div>
  );
}

function CardComponent({ config, forms }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { title, description, className = '' } = config.props as {
    title?: string;
    description?: string;
    className?: string;
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 ${className}`}>
      {(title || description) && (
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
          {title && <h3 className="text-lg font-medium text-gray-900 dark:text-white">{title}</h3>}
          {description && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</p>}
        </div>
      )}
      <div className="px-4 py-5 sm:p-6">
        {config.children?.map((child: ComponentConfig, index: number) => (
          <ComponentRenderer key={index} config={child} forms={forms} />
        ))}
      </div>
    </div>
  );
}

function SectionComponent({ config, forms }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { title, description, className = '' } = config.props as {
    title?: string;
    description?: string;
    className?: string;
  };

  return (
    <section className={`py-8 ${className}`}>
      {(title || description) && (
        <div className="mb-6">
          {title && <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>}
          {description && <p className="mt-2 text-gray-600 dark:text-gray-400">{description}</p>}
        </div>
      )}
      {config.children?.map((child: ComponentConfig, index: number) => (
        <ComponentRenderer key={index} config={child} forms={forms} />
      ))}
    </section>
  );
}

// =============================================================================
// CONTENT COMPONENTS
// =============================================================================

function HeadingComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { level = 1, text, className = '' } = config.props as {
    level?: 1 | 2 | 3 | 4 | 5 | 6;
    text: string;
    className?: string;
  };

  const Tag = `h${level}` as keyof JSX.IntrinsicElements;
  const sizeClasses: Record<number, string> = {
    1: 'text-4xl font-bold',
    2: 'text-3xl font-bold',
    3: 'text-2xl font-semibold',
    4: 'text-xl font-semibold',
    5: 'text-lg font-medium',
    6: 'text-base font-medium',
  };

  return (
    <Tag className={`${sizeClasses[level]} text-gray-900 dark:text-white ${className}`}>
      {text}
    </Tag>
  );
}

function TextComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { content, variant = 'body', className = '' } = config.props as {
    content: string;
    variant?: 'body' | 'lead' | 'small' | 'muted';
    className?: string;
  };

  const variantClasses: Record<string, string> = {
    body: 'text-base text-gray-700 dark:text-gray-300',
    lead: 'text-xl text-gray-600 dark:text-gray-400',
    small: 'text-sm text-gray-600 dark:text-gray-400',
    muted: 'text-sm text-gray-500 dark:text-gray-500',
  };

  return <p className={`${variantClasses[variant]} ${className}`}>{content}</p>;
}

function ImageComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { src, alt = '', width, height, className = '' } = config.props as {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
    className?: string;
  };

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={`max-w-full h-auto rounded-lg ${className}`}
    />
  );
}

function DividerComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { className = '' } = config.props as { className?: string };
  return <hr className={`border-gray-200 dark:border-gray-700 my-6 ${className}`} />;
}

// =============================================================================
// INTERACTIVE COMPONENTS
// =============================================================================

function ButtonComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const {
    text,
    variant = 'primary',
    size = 'md',
    onClick,
    href,
    className = '',
  } = config.props as {
    text: string;
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    onClick?: string;
    href?: string;
    className?: string;
  };

  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';

  const variantClasses: Record<string, string> = {
    primary: 'bg-primary-600 text-white hover:bg-primary-700 focus:ring-primary-500',
    secondary: 'bg-gray-100 text-gray-900 hover:bg-gray-200 focus:ring-gray-500 dark:bg-gray-700 dark:text-white',
    outline: 'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300',
    ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-500 dark:text-gray-300 dark:hover:bg-gray-800',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
  };

  const sizeClasses: Record<string, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  if (href) {
    return (
      <a href={href} className={classes}>
        {text}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={classes}
      onClick={() => {
        if (onClick) {
          // Execute action - could be navigation, form submission, etc.
          console.log('Button clicked:', onClick);
        }
      }}
    >
      {text}
    </button>
  );
}

function LinkComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { text, href, external = false, className = '' } = config.props as {
    text: string;
    href: string;
    external?: boolean;
    className?: string;
  };

  return (
    <a
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={`text-primary-600 hover:text-primary-700 hover:underline ${className}`}
    >
      {text}
    </a>
  );
}

function FormComponent({ config, forms }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { formId } = config.props as { formId: string };
  const formConfig = forms.find((f) => f.id === formId);

  if (!formConfig) {
    return (
      <div className="p-4 border border-dashed border-red-400 bg-red-50 rounded-md">
        <p className="text-sm text-red-700">Form not found: {formId}</p>
      </div>
    );
  }

  return <FormRenderer config={formConfig} />;
}

// =============================================================================
// DATA COMPONENTS
// =============================================================================

function TableComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { columns = [], data = [], className = '' } = config.props as {
    columns: Array<{ key: string; label: string; sortable?: boolean }>;
    data: Array<Record<string, unknown>>;
    className?: string;
  };

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-800">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
          {data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((col) => (
                <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {String(row[col.key] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ListComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { items = [], ordered = false, className = '' } = config.props as {
    items: string[];
    ordered?: boolean;
    className?: string;
  };

  const Tag = ordered ? 'ol' : 'ul';
  const listClass = ordered ? 'list-decimal' : 'list-disc';

  return (
    <Tag className={`${listClass} list-inside space-y-2 text-gray-700 dark:text-gray-300 ${className}`}>
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </Tag>
  );
}

function StatsComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { stats = [] } = config.props as {
    stats: Array<{ label: string; value: string | number; change?: number; changeLabel?: string }>;
  };

  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {stats.map((stat, index) => (
        <div key={index} className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{stat.label}</p>
                <p className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
            </div>
            {stat.change !== undefined && (
              <div className="mt-4">
                <span
                  className={`text-sm font-medium ${
                    stat.change >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {stat.change >= 0 ? '+' : ''}
                  {stat.change}%
                </span>
                {stat.changeLabel && (
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">{stat.changeLabel}</span>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChartComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { type = 'bar', title, className = '' } = config.props as {
    type?: 'bar' | 'line' | 'pie' | 'doughnut';
    title?: string;
    className?: string;
  };

  // Placeholder for chart - would integrate with Chart.js or similar
  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow p-6 ${className}`}>
      {title && <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{title}</h3>}
      <div className="h-64 flex items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-gray-500 dark:text-gray-400">
          Chart ({type}) - Integrate charting library
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// NAVIGATION COMPONENTS
// =============================================================================

function TabsComponent({ config, forms }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { tabs = [], defaultTab = 0 } = config.props as {
    tabs: Array<{ label: string; content: ComponentConfig[] }>;
    defaultTab?: number;
  };

  // Simple tab implementation - would use state for active tab
  const activeTab = tabs[defaultTab];

  return (
    <div>
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab, index) => (
            <button
              key={index}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                index === defaultTab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      <div className="py-4">
        {activeTab?.content.map((child: ComponentConfig, index: number) => (
          <ComponentRenderer key={index} config={child} forms={forms} />
        ))}
      </div>
    </div>
  );
}

function BreadcrumbComponent({ config }: { config: ComponentConfig; forms: FormConfig[] }) {
  const { items = [] } = config.props as {
    items: Array<{ label: string; href?: string }>;
  };

  return (
    <nav className="flex" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {items.map((item, index) => (
          <li key={index} className="flex items-center">
            {index > 0 && (
              <svg className="h-5 w-5 text-gray-400 mx-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            {item.href ? (
              <a href={item.href} className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400">
                {item.label}
              </a>
            ) : (
              <span className="text-sm text-gray-900 dark:text-white font-medium">{item.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
